import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import api from '../utils/api';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import * as HighchartsExporting from 'highcharts/modules/exporting';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    FiCalendar,
    FiChevronDown,
    FiChevronUp,
    FiFilter,
    FiRotateCcw,
    FiDownload,
    FiClock,
    FiBarChart2,
    FiX,
    FiExternalLink,
    FiChevronLeft,
    FiChevronRight,
    FiSearch,
    FiFileText,
    FiMaximize2,
    FiActivity,
    FiZap,
    FiCopy,
    FiMove,
    FiTrendingUp,
    FiAlertTriangle
} from 'react-icons/fi';
import RepeatTrendChart from './RepeatTrendChart';
import TestingRejectStatusChart from './TestingRejectStatusChart';

if (typeof HighchartsExporting === 'function') {
    HighchartsExporting(Highcharts);
} else if (HighchartsExporting && typeof HighchartsExporting.default === 'function') {
    HighchartsExporting.default(Highcharts);
}

const COLORS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#EC4899', '#6366F1', '#A855F7', '#F97316'
];

const DatePillInput = forwardRef(({ value, onClick, label }, ref) => (
    <button
        type="button"
        ref={ref}
        onClick={onClick}
        className="min-w-[118px] h-10 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-[12px] font-bold tracking-tight flex items-center gap-2 shadow-sm hover:border-slate-300 transition-colors"
    >
        <FiCalendar className="text-emerald-500 text-sm shrink-0" />
        <span className="whitespace-nowrap">{value || label}</span>
    </button>
));
DatePillInput.displayName = 'DatePillInput';

const Leadtickettrends = () => {
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedYear, setSelectedYear] = useState('all');

    const [trendSelectedMonth, setTrendSelectedMonth] = useState('all');
    const [trendSelectedYear, setTrendSelectedYear] = useState('all');
    const [delaySelectedMonth, setDelaySelectedMonth] = useState('all');
    const [delaySelectedYear, setDelaySelectedYear] = useState('all');

    const startDate = useMemo(() => {
        if (selectedYear === 'all') return new Date(2020, 0, 1);
        return selectedMonth === 'all' ? new Date(selectedYear, 0, 1) : startOfMonth(new Date(selectedYear, selectedMonth));
    }, [selectedYear, selectedMonth]);

    const endDate = useMemo(() => {
        if (selectedYear === 'all') return new Date();
        return selectedMonth === 'all' ? new Date(selectedYear, 11, 31) : endOfMonth(new Date(selectedYear, selectedMonth));
    }, [selectedYear, selectedMonth]);

    const trendStartDate = useMemo(() => {
        if (trendSelectedYear === 'all') return new Date(2020, 0, 1);
        return trendSelectedMonth === 'all' ? new Date(trendSelectedYear, 0, 1) : startOfMonth(new Date(trendSelectedYear, trendSelectedMonth));
    }, [trendSelectedYear, trendSelectedMonth]);

    const trendEndDate = useMemo(() => {
        if (trendSelectedYear === 'all') return new Date();
        return trendSelectedMonth === 'all' ? new Date(trendSelectedYear, 11, 31) : endOfMonth(new Date(trendSelectedYear, trendSelectedMonth));
    }, [trendSelectedYear, trendSelectedMonth]);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        statuses: [],
        pieChartData: [],
        employeeStatusTable: [],
        durationTable: [],
        newTicketsTrend: { data: [], grandTotal: 0 }
    });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'employee_name', direction: 'asc' });
    const [modalConfig, setModalConfig] = useState({ show: false, tickets: [], chartData: [], loading: false, title: '' });
    const [modalPage, setModalPage] = useState(1);
    const [modalSearch, setModalSearch] = useState('');
    const [delayAnalytics, setDelayAnalytics] = useState({ aging: [], waiting: [] });
    const [delayLoading, setDelayLoading] = useState(false);
    const [modalItemsPerPage, setModalItemsPerPage] = useState(10);
    const [modalSortConfig, setModalSortConfig] = useState({ key: 'id', direction: 'desc' });
    const [copiedTicketId, setCopiedTicketId] = useState(null);
    const [modalAssigneeFilter, setModalAssigneeFilter] = useState(null); // Local filter for drilldown chart
    const [showDrilldownChart, setShowDrilldownChart] = useState(false);
    const [drilldownChartHeight, setDrilldownChartHeight] = useState(380); // Balanced default for better table visibility
    const [isResizingDrilldown, setIsResizingDrilldown] = useState(false);
    const [repeatTrends, setRepeatTrends] = useState([]);
    const [testingRejectTrends, setTestingRejectTrends] = useState([]);
    const [trendsLoading, setTrendsLoading] = useState(false);
    const [newTrendsSelectedMonth, setNewTrendsSelectedMonth] = useState('all');
    const [newTrendsSelectedYear, setNewTrendsSelectedYear] = useState('all');

    const handleModalSort = (key) => {
        let direction = 'asc';
        if (modalSortConfig.key === key && modalSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setModalSortConfig({ key, direction });
    };

    const filteredModalTickets = useMemo(() => {
        let tickets = modalConfig.tickets;

        // Apply Assignee Filter from Chart
        if (modalAssigneeFilter) {
            tickets = tickets.filter(t => String(t.assign_id) === String(modalAssigneeFilter.id));

            // Apply Origin source filtering (Internal/External) if applicable
            if (modalAssigneeFilter.source && modalAssigneeFilter.source !== 'Overall') {
                tickets = tickets.filter(t =>
                    String(t.origin).toLowerCase() === String(modalAssigneeFilter.source).toLowerCase()
                );
            }
        }

        if (!modalSearch) return tickets;
        const search = modalSearch.toLowerCase().trim();
        return tickets.filter(t =>
            String(t.id).toLowerCase().includes(search) ||
            String(t.title).toLowerCase().includes(search) ||
            String(t.status).toLowerCase().includes(search) ||
            String(t.customer_code || '').toLowerCase().includes(search) ||
            String(t.currently_working || '').toLowerCase().includes(search) ||
            String(t.collaborators || '').toLowerCase().includes(search) ||
            String(t.origin || '').toLowerCase().includes(search) ||
            String(t.aging_display || '').toLowerCase().includes(search)
        );
    }, [modalConfig.tickets, modalSearch, modalAssigneeFilter]);

    const sortedModalTickets = useMemo(() => {
        let sortableItems = [...filteredModalTickets];
        if (modalSortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[modalSortConfig.key] || '';
                let bValue = b[modalSortConfig.key] || '';

                // Numeric sort for ID
                if (modalSortConfig.key === 'id') {
                    aValue = parseInt(aValue);
                    bValue = parseInt(bValue);
                } else if (modalSortConfig.key === 'created_date' || modalSortConfig.key === 'updated_date') {
                    aValue = new Date(aValue).getTime();
                    bValue = new Date(bValue).getTime();
                }

                if (aValue < bValue) return modalSortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return modalSortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredModalTickets, modalSortConfig]);

    const fetchTicketDetails = async (empId, empName, statusVal = null, bucket = null, custKey = null, originType = null) => {
        setModalPage(1); // Reset pagination on new drill-down

        let displayTitle = empName ? `${empName}` : 'All Tickets';
        if (statusVal) displayTitle = `Ticket By Status (${empName}) - ${statusVal}`;
        else if (bucket) displayTitle = `Aging: ${bucket.replace('_', ' ')} days (${empName})`;
        else if (originType === 'external') displayTitle = `External Tickets (${empName})`;
        else if (originType === 'internal') displayTitle = `Internal Tickets (${empName})`;
        else if (custKey) displayTitle = `Tickets for ${custKey} (Customer Metrics)`;

        setModalConfig(prev => ({ ...prev, show: true, loading: true, tickets: [], chartData: [], title: displayTitle, type: undefined }));
        setModalAssigneeFilter(null); // Reset local filter for new primary drilldown
        setDrilldownChartHeight(380); // Always start with balanced size for new counts
        setShowDrilldownChart(false);  // Always show chart initially for new counts
        try {
            const statusSlug = statusVal && isNaN(statusVal)
                ? statusVal.toLowerCase().trim().replace(/\s+/g, '_')
                : statusVal;

            const res = await api.get('tickets/analytics-drilldown', {
                params: {
                    employee_id: empId,
                    customer_key: custKey,
                    status: statusSlug,
                    aging_bucket: bucket,
                    origin_type: originType,
                    date_from: custKey ? format(trendStartDate, 'yyyy-MM-dd') : format(startDate, 'yyyy-MM-dd'),
                    date_to: custKey ? format(trendEndDate, 'yyyy-MM-dd') : format(endDate, 'yyyy-MM-dd')
                }
            });

            let reportTitle = "Direct Ticket Analytics";
            if (custKey) {
                reportTitle = `New Tickets - ${empName} (Customer)`;
            } else if (bucket) {
                let bucketLabel = bucket;
                if (bucket === 'less_5') bucketLabel = '< 5 Days';
                else if (bucket === '5_10') bucketLabel = '5 - 10 Days';
                else if (bucket === 'more_10') bucketLabel = '> 10 Days';
                else bucketLabel = bucket.replace('_', ' ').toUpperCase();
                reportTitle = `Tickets By Duration (${empName}) ${bucketLabel}`;
            } else if (statusVal) {
                reportTitle = `Ticket By Status (${empName}) - ${statusVal}`;
            } else {
                reportTitle = `Total Opened Tickets (${empName})`;
            }

            setModalConfig(prev => ({
                ...prev,
                loading: false,
                tickets: res.data.tickets || [],
                chartData: res.data.chartData || [],
                title: reportTitle,
                type: undefined,
                originalParams: { empId, empName, statusVal, bucket, custKey, originType } // Store for breadcrumbs/back
            }));
        } catch (error) {
            console.error('Drill-down fetch failed:', error);
            setModalConfig(prev => ({ ...prev, loading: false }));
            toast.error('Unable to fetch detailed ticket metrics');
        }
    };

    const handleAssigneeDrilldown = (assigneeId, assigneeName, targetSource = null) => {
        // Instant filter instead of re-fetching
        if (modalAssigneeFilter && modalAssigneeFilter.id === assigneeId && modalAssigneeFilter.source === targetSource) {
            setModalAssigneeFilter(null); // Toggle off if clicking same person in same context
            toast.success("Showing all tickets");
        } else {
            setModalAssigneeFilter({ id: assigneeId, name: assigneeName, source: targetSource });
            setModalPage(1);
            const context = targetSource && targetSource !== 'Overall' ? `${targetSource} ` : '';
            toast.success(`Showing ${context}tickets for ${assigneeName}`);
        }
    };

    const handleResize = useCallback((e) => {
        if (!e.buttons) {
            setIsResizingDrilldown(false);
            window.removeEventListener('mousemove', handleResize);
            return;
        }

        const modalBody = document.getElementById('drilldown-modal-body');
        if (!modalBody) return;

        const rect = modalBody.getBoundingClientRect();
        const availableHeight = rect.height;
        const newHeight = e.clientY - rect.top - 20; // Exact offset relative to body start

        // Boundary safety: Keep at least 120px for chart and 200px for table
        if (newHeight > 120 && newHeight < availableHeight - 200) {
            setDrilldownChartHeight(newHeight);
        }
    }, []);

    const startResizing = useCallback((e) => {
        e.preventDefault();
        setIsResizingDrilldown(true);
        window.addEventListener('mousemove', handleResize);
        window.addEventListener('mouseup', () => {
            setIsResizingDrilldown(false);
            window.removeEventListener('mousemove', handleResize);
        }, { once: true });
    }, [handleResize]);

    const drilldownSubCharts = useMemo(() => {
        const getSummary = (list) => {
            const summary = {};
            list.forEach(t => {
                const name = t.currently_working || t.employee_name || 'Unassigned';
                const id = t.assign_id || 0;
                if (!summary[name]) summary[name] = { name, id, y: 0 };
                summary[name].y++;
            });
            return Object.values(summary).sort((a, b) => b.y - a.y);
        };

        return {
            overall: modalConfig.chartData,
            internal: getSummary(modalConfig.tickets.filter(t => t.origin === 'Internal')),
            external: getSummary(modalConfig.tickets.filter(t => t.origin === 'External'))
        };
    }, [modalConfig.tickets, modalConfig.chartData]);

    const activeDonutCharts = useMemo(() => {
        const list = [
            { id: 'overall', label: 'Overall', labelFull: 'Overall Distribution', data: drilldownSubCharts.overall, colorSet: COLORS, badge: 'Full Scope', badgeColor: 'blue' },
            { id: 'internal', label: 'Internal', labelFull: 'Internal Tickets', data: drilldownSubCharts.internal, colorSet: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'], badge: 'Team Internal', badgeColor: 'emerald' },
            { id: 'external', label: 'External', labelFull: 'External Tickets', data: drilldownSubCharts.external, colorSet: ['#f59e0b', '#fbbf24', '#fcd34d', '#fef3c7', '#fffbeb'], badge: 'Client Facing', badgeColor: 'amber' }
        ];
        return list.filter(c => c.data && c.data.length > 0);
    }, [drilldownSubCharts]);

    const getPremiumDonutOptions = (data, label, colorSet) => {
        const total = data.reduce((sum, item) => sum + item.y, 0);
        return {
            chart: {
                type: 'pie',
                backgroundColor: '#FFFFFF',
                height: 265,
                spacing: [12, 15, 12, 15],
                style: { fontFamily: 'inherit' }
            },
            title: {
                text: `<div style="text-align: center">
                        <span style="font-size: 8px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px">${label}</span><br/>
                        <span style="font-size: 16px; font-weight: 900; color: #0f172a">${total}</span><br/>
                        <span style="font-size: 7px; font-weight: 800; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.5px">Tickets</span>
                       </div>`,
                align: 'center',
                verticalAlign: 'middle',
                y: 8,
                useHTML: true
            },
            tooltip: {
                pointFormat: '<b>{point.name}</b>: {point.y} Tickets',
                backgroundColor: '#ffffff',
                borderWidth: 1,
                borderColor: '#e2e8f0',
                shadow: true,
                style: { fontSize: '10px', color: '#334155' }
            },
            plotOptions: {
                pie: {
                    innerSize: '72%',
                    colors: colorSet || COLORS,
                    dataLabels: {
                        enabled: true,
                        allowOverlap: true,
                        overflow: 'allow',
                        format: '<span style="font-weight: 900; color: #475569">{point.name}</span>: <span style="font-weight: 900; color: #ef4444">{point.y}</span>',
                        style: {
                            fontSize: '8px',
                            textOutline: 'none',
                            fontWeight: '900'
                        },
                        padding: 0,
                        connectorWidth: 1.2,
                        connectorColor: '#cbd5e1',
                        distance: 22,
                        overflow: 'allow'
                    },
                    showInLegend: false,
                    borderWidth: 3,
                    borderColor: '#ffffff',
                    point: {
                        events: {
                            click: function () {
                                if (this.id) handleAssigneeDrilldown(this.id, this.name, label);
                            }
                        }
                    }
                }
            },
            credits: { enabled: false },
            series: [{ data: data }]
        };
    };

    const resetDrilldownSize = () => {
        setDrilldownChartHeight(380);
        toast.success("View reset to standard");
    };

    const drilldownChartOptions = useMemo(() => {
        return {
            chart: {
                type: 'pie',
                backgroundColor: '#FFFFFF',
                height: 265,
                spacing: [10, 12, 10, 12],
                margin: [0, 0, 0, 0],
                style: { fontFamily: 'inherit' }
            },
            title: {
                text: `<div style="text-align: center"><span style="font-size: 14px; font-weight: 900; color: #ef4444">${modalConfig.tickets.length}</span><br/><span style="font-size: 8px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px">Total</span></div>`,
                align: 'center',
                verticalAlign: 'middle',
                y: 5,
                useHTML: true
            },
            plotOptions: {
                pie: {
                    innerSize: '72%',
                    colors: COLORS,
                    dataLabels: {
                        enabled: true,
                        allowOverlap: true,
                        overflow: 'allow',
                        format: '<span style="font-weight: 900; color: #475569">{point.name}</span>: <span style="font-weight: 900; color: #ef4444">{point.y}</span>',
                        style: {
                            fontSize: '8px',
                            textOutline: 'none',
                            fontWeight: '900'
                        },
                        padding: 0,
                        connectorWidth: 1.2,
                        connectorColor: '#cbd5e1',
                        distance: 22,
                        overflow: 'allow'
                    },
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    point: {
                        events: {
                            click: function () {
                                if (this.id) {
                                    handleAssigneeDrilldown(this.id, this.name);
                                }
                            }
                        }
                    },
                    states: {
                        hover: {
                            brightness: 0.1,
                            halo: { size: 10 }
                        }
                    }
                }
            },
            series: [{
                name: 'Tickets',
                data: modalConfig.chartData,
                enableMouseTracking: true
            }],
            credits: { enabled: false },
            tooltip: {
                headerFormat: '',
                pointFormat: '<span style="color:{point.color}; font-size: 14px">\u25CF</span> <span style="font-size: 11px; font-weight: 900">{point.name}</span>: <b>{point.y} Tickets</b>',
                backgroundColor: '#ffffff',
                borderWidth: 0,
                borderRadius: 12,
                shadow: {
                    color: 'rgba(0,0,0,0.1)',
                    offsetX: 0,
                    offsetY: 4,
                    blur: 12
                },
                padding: 12
            }
        };
    }, [modalConfig.chartData, modalConfig.tickets.length]);

    const handleCopyTitle = (id, text) => {
        navigator.clipboard.writeText(text);
        setCopiedTicketId(id);
        setTimeout(() => setCopiedTicketId(null), 2000);
    };

    const exportDrilldownToExcel = async (reportData, title) => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Detail Report');
            const isCustMetric = title.includes("(Customer)");
            const isTestingReject = title.includes("Testing Rejected");

            let columns = [
                { header: 'S.No', key: 'sno', width: 6 },
                { header: 'Ticket ID', key: 'id', width: 12 },
                { header: 'Title', key: 'title', width: 55 },
                { header: 'Origin', key: 'origin', width: 12 },
                ...(isCustMetric ? [] : [{ header: 'Customer', key: 'customer_code', width: 12 }]),
                { header: 'Status', key: 'status', width: 15 },
                { header: 'Issue Type', key: 'issue_type', width: 18 },
                { header: 'Assigned To', key: 'currently_working', width: 22 },
            ];

            // Conditionally add Aging and handle Collaborators name
            if (!isTestingReject) {
                columns.push({ header: 'Total Aging', key: 'aging_display', width: 15 });
                columns.push({ header: 'On Hold Time', key: 'on_hold_display', width: 15 });
                columns.push({ header: 'Active Aging', key: 'net_aging_display', width: 15 });
                columns.push({ header: 'All Collaborators', key: 'collaborators', width: 25 });
            } else {
                columns.push({ header: 'Tested By', key: 'collaborators', width: 25 });
            }

            columns.push({ header: 'Created At', key: 'created_date', width: 25 });
            columns.push({ header: isTestingReject ? 'Last Updated' : 'Updated At', key: 'updated_date', width: 25 });

            const totalCols = columns.length;
            const colRange = String.fromCharCode(64 + totalCols);

            // Row 1: Company Branding
            worksheet.mergeCells(`A1:${colRange}1`);
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Row 2: Report Title & Date
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells(`A2:${colRange}2`);
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Row 3: Table Headers
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col.header;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            });
            headerRow.height = 25;

            // Set column widths properly after rows are added
            columns.forEach((col, i) => {
                const actualWidth = (col.key === 'title') ? 75 : (col.key === 'collaborators') ? 40 : col.width;
                worksheet.getColumn(i + 1).width = actualWidth;
            });

            // Data Rows
            reportData.forEach((t, i) => {
                const rowValues = [
                    i + 1,
                    `#${t.id}`,
                    t.title,
                    t.origin,
                ];
                if (!isCustMetric) rowValues.push(t.customer_code || 'N/A');

                rowValues.push(t.status);
                rowValues.push(t.issue_type || '—');
                rowValues.push(t.currently_working || 'N/A');

                if (!isTestingReject) {
                    rowValues.push(t.aging_display || 'N/A');
                    rowValues.push(t.on_hold_display ? t.on_hold_display : '-');
                    rowValues.push(t.net_aging_display || t.aging_display || 'N/A');
                }

                rowValues.push(t.collaborators || 'N/A');
                rowValues.push(t.created_date ? format(new Date(t.created_date), 'dd/MM/yyyy HH:mm:ss') : 'N/A');
                rowValues.push(t.updated_date ? format(new Date(t.updated_date), 'dd/MM/yyyy HH:mm:ss') : 'N/A');

                const dataRow = worksheet.addRow(rowValues);

                dataRow.eachCell((cell, colIndex) => {
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' },
                        bottom: { style: 'thin' }, right: { style: 'thin' }
                    };

                    // Logic to find Title and Collaborators column index
                    const isTitle = colIndex === 3;
                    const isCollab = isTestingReject
                        ? (isCustMetric ? colIndex === 7 : colIndex === 8)
                        : (isCustMetric ? colIndex === 8 : colIndex === 9);

                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: (isTitle || isCollab) ? 'left' : 'center',
                        wrapText: (isTitle || isCollab)
                    };

                    if (colIndex === 2) {
                        cell.font = { color: { argb: 'FFEF4444' }, bold: true };
                    }
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `Ticket_Export_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
            anchor.click();
            toast.success('Excel Report Downloaded');
        } catch (error) {
            console.error('Excel error:', error);
            toast.error('Failed to generate Excel');
        }
    };

    const exportDrilldownToPDF = (reportData, title) => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const isCustMetric = title.includes("(Customer)");
            const isTestingReject = title.includes("Testing Rejected");

            // Build headers dynamically
            let headers = ['S.No', 'Ticket ID', 'Title', 'Origin'];
            if (!isCustMetric) headers.push('Customer');
            headers.push('Status', 'Issue Type', 'Assigned To');
            if (!isTestingReject) {
                headers.push('Total Aging');
                headers.push('On Hold Time');
                headers.push('Active Aging');
            }
            headers.push(isTestingReject ? 'Tested By' : 'Collaborators');
            headers.push('Created', isTestingReject ? 'Updated' : 'Updated');

            const head = [headers];

            const body = reportData.map((t, idx) => {
                let row = [
                    idx + 1,
                    `#${t.id}`,
                    t.title,
                    t.origin,
                ];
                if (!isCustMetric) row.push(t.customer_code || 'N/A');

                row.push(t.status);
                row.push(t.issue_type || '—');
                row.push(t.currently_working || 'N/A');

                if (!isTestingReject) {
                    row.push(t.aging_display || 'N/A');
                    row.push(t.on_hold_display ? t.on_hold_display : '-');
                    row.push(t.net_aging_display || t.aging_display || 'N/A');
                }

                row.push(t.collaborators || 'N/A');
                row.push(t.created_date ? format(new Date(t.created_date), 'dd/MM/yyyy') : 'N/A');
                row.push(t.updated_date ? format(new Date(t.updated_date), 'dd/MM/yyyy') : 'N/A');

                return row;
            });

            autoTable(doc, {
                head,
                body,
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                theme: 'grid',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'center',
                    cellPadding: 8
                },
                bodyStyles: { fontSize: 8, cellPadding: 6, textColor: [51, 65, 85] },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 35 },
                    1: { textColor: [180, 0, 0], fontStyle: 'bold', cellWidth: 55, halign: 'center' },
                    2: { cellWidth: 'auto' }
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Company Name - Red, Bold, Centered
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });

                    // Report Title
                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    // Date
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    // Separator line
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);

                    // Footer line
                    doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);

                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(`Report Date: ${now}`, 32, pageHeight - 22);
                    doc.text(`Page ${data.pageNumber} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            doc.save(`Ticket_Drilldown_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('PDF Drilldown Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const handleExportPDF = async () => {
        try {
            const loadingToast = toast.loading('Generating Dashboard Report...');
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');

            const addHeader = (title) => {
                doc.setFontSize(18);
                doc.setTextColor(180, 0, 0);
                doc.setFont('helvetica', 'bold');
                doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });
                doc.setFontSize(13);
                doc.setTextColor(20, 20, 20);
                doc.setFont('helvetica', 'bold');
                doc.text(title, pageWidth / 2, 58, { align: 'center' });
                doc.setFontSize(9);
                doc.setTextColor(100, 100, 100);
                doc.setFont('helvetica', 'normal');
                doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.5);
                doc.line(30, 88, pageWidth - 30, 88);
            };

            const addFooter = (pageNum) => {
                doc.setDrawColor(200, 200, 200);
                doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);
                doc.setFontSize(8);
                doc.setTextColor(120, 120, 120);
                doc.text(`Report Date: ${now}`, 32, pageHeight - 22);
                doc.text(`Page ${pageNum} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });
            };

            addHeader('Lead Ticket Trends - Overall Summary');

            const chartDiv = document.querySelector('.highcharts-container');
            if (chartDiv) {
                const canvas = await html2canvas(chartDiv, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
                const imgData = canvas.toDataURL('image/png');
                doc.addImage(imgData, 'PNG', 40, 100, pageWidth - 80, 180);
            }

            autoTable(doc, {
                startY: 290,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['Employee', 'Opened', ...filteredStatuses.map(s => s.status_name), 'Pending Customer Verification & Confirmation']],
                body: sortedEmployeeData.map(row => [
                    row.employee_name,
                    row.total,
                    ...filteredStatuses.map(s => getStatusValue(row, s.status_id, s.status_name)),
                    getPendingCustValue(row)
                ]),
                foot: [['Total', employeeTotals.total, ...filteredStatuses.map(s => employeeTotals[`status_${s.status_id}`] || 0), employeeTotals.pendingCust]],
                theme: 'grid',
                headStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontSize: 8 },
                footStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontSize: 8 },
                styles: { fontSize: 7, cellPadding: 5 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    if (data.pageNumber > 1) {
                        addHeader('Lead Ticket Trends - Overall Summary');
                    }
                    addFooter(data.pageNumber);
                }
            });

            doc.addPage();
            addHeader('Ticket Aging Analysis');

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['Employee Name', '< 5 Days', '5 - 10 Days', '> 10 Days', 'Total Open']],
                body: filteredDurationTable.map(row => [
                    row.employee_name,
                    row.bucket_less_5,
                    row.bucket_5_10,
                    row.bucket_more_10,
                    Number(row.bucket_less_5 || 0) + Number(row.bucket_5_10 || 0) + Number(row.bucket_more_10 || 0)
                ]),
                foot: [['Total Aging', durationTotals.bucket_less_5, durationTotals.bucket_5_10, durationTotals.bucket_more_10, (durationTotals.bucket_less_5 + durationTotals.bucket_5_10 + durationTotals.bucket_more_10)]],
                theme: 'grid',
                headStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontSize: 9 },
                footStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontSize: 9 },
                styles: { fontSize: 8, cellPadding: 6 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    if (data.pageNumber > doc.internal.getNumberOfPages() - 1) {
                        // addHeader already called for first page of section
                    }
                    addFooter(data.pageNumber);
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            toast.dismiss(loadingToast);
            doc.save(`Dashboard_Report_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('Full Dashboard PDF Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate Dashboard PDF');
        }
    };

    const exportStatusTableToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Ticket By Status');
            const columns = ['Employee', 'Opened', 'External', 'Internal', ...filteredStatuses.map(s => s.status_name), 'Pending Customer Verification & Confirmation'];
            const totalCols = columns.length;
            const colRange = String.fromCharCode(64 + totalCols);

            // Row 1: Brand
            worksheet.mergeCells(`A1:${colRange}1`);
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Row 2: Title
            const title = "Employee Ticket Status Report";
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells(`A2:${colRange}2`);
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Row 3: Header
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            headerRow.height = 25;

            // Data
            sortedEmployeeData.forEach((row, rowIndex) => {
                const excelRow = worksheet.getRow(rowIndex + 4);
                const rowData = [
                    row.employee_name,
                    row.total || 0,
                    row.external || 0,
                    row.internal || 0,
                    ...filteredStatuses.map(s => getStatusValue(row, s.status_id, s.status_name)),
                    getPendingCustValue(row)
                ];

                rowData.forEach((val, colIndex) => {
                    const cell = excelRow.getCell(colIndex + 1);
                    cell.value = val;
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    cell.alignment = { horizontal: colIndex === 0 ? 'left' : 'center', vertical: 'middle' };
                    if (rowIndex % 2 === 0) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                    }
                });
                excelRow.height = 22;
            });

            // Footer
            const footerRow = worksheet.getRow(sortedEmployeeData.length + 4);
            const footerData = ['Total', employeeTotals.total, employeeTotals.external, employeeTotals.internal, ...filteredStatuses.map(s => employeeTotals[`status_${s.status_id}`] || 0), employeeTotals.pendingCust];
            footerData.forEach((val, colIndex) => {
                const cell = footerRow.getCell(colIndex + 1);
                cell.value = val;
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: colIndex === 0 ? 'left' : 'center', vertical: 'middle' };
            });
            footerRow.height = 25;

            // Widths
            worksheet.getColumn(1).width = 30;
            for (let i = 2; i <= totalCols; i++) worksheet.getColumn(i).width = 12;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `Ticket_Status_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
            toast.success('Status Excel Exported');
        } catch (error) {
            console.error('Excel error:', error);
            toast.error('Failed to generate Status Excel');
        }
    };

    const exportStatusTableToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const title = "Ticket By Status-Report";

            autoTable(doc, {
                startY: 115,
                margin: { top: 115, left: 30, right: 30, bottom: 50 },
                head: [['Employee', 'Opened', 'External', 'Internal', ...filteredStatuses.map(s => s.status_name), 'Pending Customer Verification & Confirmation']],
                body: sortedEmployeeData.map(row => [
                    row.employee_name,
                    row.total || 0,
                    row.external || 0,
                    row.internal || 0,
                    ...filteredStatuses.map(s => getStatusValue(row, s.status_id, s.status_name)),
                    getPendingCustValue(row)
                ]),
                foot: [['Total', employeeTotals.total, employeeTotals.external, employeeTotals.internal, ...filteredStatuses.map(s => employeeTotals[`status_${s.status_id}`] || 0), employeeTotals.pendingCust]],
                theme: 'grid',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 8.5,
                    halign: 'center',
                    cellPadding: { top: 7, bottom: 7, left: 5, right: 5 },
                    lineColor: [80, 80, 80],
                    lineWidth: 0.5
                },
                footStyles: {
                    fillColor: [230, 230, 230],
                    textColor: [20, 20, 20],
                    fontStyle: 'bold',
                    fontSize: 8.5,
                    lineColor: [180, 180, 180],
                    lineWidth: 0.5
                },
                styles: {
                    fontSize: 7.5,
                    cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
                    lineColor: [200, 200, 200],
                    lineWidth: 0.3,
                    textColor: [40, 40, 40]
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                columnStyles: {
                    0: { halign: 'left', cellWidth: 80 },
                    1: { halign: 'right', cellWidth: 40 },
                    2: { halign: 'right', cellWidth: 40 },
                    3: { halign: 'right', cellWidth: 40 }
                },
                didParseCell: (data) => {
                    if ((data.section === 'body' || data.section === 'foot') && data.column.index >= 1) {
                        data.cell.styles.halign = 'right';
                    }
                    if (data.section === 'foot' && data.column.index === 0) {
                        data.cell.styles.halign = 'left';
                    }
                },
                didDrawPage: (data) => {
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();

                    // Company Name - Red, Bold, Centered
                    doc.setFontSize(20);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 38, { align: 'center' });

                    // Report Title - Black, Bold, Centered
                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageWidth / 2, 60, { align: 'center' });

                    // Date subtitle
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 78, { align: 'center' });

                    // Separator line
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);

                    // Footer line
                    doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);

                    // Footer - centered date
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${now}`, 32, pageHeight - 25);

                    // Page number - right
                    const str = `Page ${data.pageNumber} of {total_pages_count_string}`;
                    doc.text(str, pageWidth - 32, pageHeight - 25, { align: 'right' });
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            doc.save(`Ticket_By_Status_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('PDF Report Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const exportTrendToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('New Tickets Trend');
            const columns = ['S.No', 'Customer', 'New Tickets', 'Total Tickets', '% of New'];
            const totalCols = columns.length;
            const colRange = String.fromCharCode(64 + totalCols);

            // Brand Header
            worksheet.mergeCells(`A1:${colRange}1`);
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Report Title
            const title = "New Tickets Trend Report";
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells(`A2:${colRange}2`);
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Table Header
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            headerRow.height = 25;

            let totalNew = 0, totalOverall = 0;
            trendData.forEach((row, rowIndex) => {
                totalNew += row.newCount;
                totalOverall += row.totalCount;
                const excelRow = worksheet.getRow(rowIndex + 4);
                const rowData = [rowIndex + 1, row.customer, row.newCount, row.totalCount, `${row.percentage}%`];

                rowData.forEach((val, colIndex) => {
                    const cell = excelRow.getCell(colIndex + 1);
                    cell.value = val;
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    cell.alignment = { horizontal: colIndex === 1 ? 'left' : 'center', vertical: 'middle' };
                    if (rowIndex % 2 === 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                });
                excelRow.height = 22;
            });

            const footerRow = worksheet.getRow(trendData.length + 4);
            const footerData = ['Total', '', totalNew, totalOverall, '—'];
            footerData.forEach((val, colIndex) => {
                const cell = footerRow.getCell(colIndex + 1);
                cell.value = val;
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: colIndex === 0 ? 'left' : 'center', vertical: 'middle' };
            });
            footerRow.height = 25;

            worksheet.getColumn(1).width = 8;
            worksheet.getColumn(2).width = 40;
            worksheet.getColumn(3).width = 15;
            worksheet.getColumn(4).width = 15;
            worksheet.getColumn(5).width = 15;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `New_Tickets_Trend_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
            toast.success('Trend Excel Downloaded');
        } catch (error) {
            console.error('Trend Excel error:', error);
            toast.error('Failed to generate Trend Excel');
        }
    };

    const exportTrendToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const title = "New Tickets Trend Report";

            let totalNew = 0;
            let totalOverall = 0;
            const bodyData = trendData.map((row, idx) => {
                totalNew += row.newCount;
                totalOverall += row.totalCount;
                return [
                    idx + 1,
                    row.customer,
                    row.newCount,
                    row.totalCount,
                    `${row.percentage}%`
                ];
            });

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['S.No', 'Customer', 'New Tickets', 'Total Tickets', '% of New']],
                body: bodyData,
                foot: [['Total', '', totalNew, totalOverall, '—']],
                theme: 'grid',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 9,
                    halign: 'center',
                    cellPadding: 8
                },
                footStyles: {
                    fillColor: [230, 230, 230],
                    textColor: [20, 20, 20],
                    fontStyle: 'bold',
                    fontSize: 9,
                    lineColor: [180, 180, 180],
                    lineWidth: 0.5
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 6,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.3,
                    textColor: [40, 40, 40]
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 40 },
                    1: { halign: 'left' },
                    2: { halign: 'right', cellWidth: 85 },
                    3: { halign: 'right', cellWidth: 85 },
                    4: { halign: 'right', cellWidth: 70 }
                },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Company Name
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });

                    // Report Title
                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    // Date
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    // Separator
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);

                    // Footer
                    doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(`Report Date: ${now}`, 32, pageHeight - 22);
                    doc.text(`Page ${data.pageNumber} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            doc.save(`New_Tickets_Trend_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('PDF Trend Report Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const exportDurationTableToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Tickets By Duration');
            const columns = ['Employee', '< 5 Days', '5 - 10 Days', 'Critical (>10d)', 'Total Open'];
            const totalCols = columns.length;
            const colRange = String.fromCharCode(64 + totalCols);

            // Brand Header
            worksheet.mergeCells(`A1:${colRange}1`);
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Report Title
            const title = "Tickets By Duration Report";
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells(`A2:${colRange}2`);
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Table Header
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            headerRow.height = 25;

            filteredDurationTable.forEach((row, rowIndex) => {
                const excelRow = worksheet.getRow(rowIndex + 4);
                const rowData = [
                    row.employee_name,
                    row.bucket_less_5 || 0,
                    row.bucket_5_10 || 0,
                    row.bucket_more_10 || 0,
                    Number(row.bucket_less_5 || 0) + Number(row.bucket_5_10 || 0) + Number(row.bucket_more_10 || 0)
                ];

                rowData.forEach((val, colIndex) => {
                    const cell = excelRow.getCell(colIndex + 1);
                    cell.value = val;
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    cell.alignment = { horizontal: colIndex === 0 ? 'left' : 'center', vertical: 'middle' };
                    if (rowIndex % 2 === 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                });
                excelRow.height = 22;
            });

            const footerRow = worksheet.getRow(filteredDurationTable.length + 4);
            const footerData = ['Total', durationTotals.bucket_less_5, durationTotals.bucket_5_10, durationTotals.bucket_more_10, (durationTotals.bucket_less_5 + durationTotals.bucket_5_10 + durationTotals.bucket_more_10)];
            footerData.forEach((val, colIndex) => {
                const cell = footerRow.getCell(colIndex + 1);
                cell.value = val;
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: colIndex === 0 ? 'left' : 'center', vertical: 'middle' };
            });
            footerRow.height = 25;

            worksheet.getColumn(1).width = 30;
            for (let i = 2; i <= totalCols; i++) worksheet.getColumn(i).width = 15;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `Tickets_By_Duration_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
            toast.success('Duration Excel Downloaded');
        } catch (error) {
            console.error('Excel error:', error);
            toast.error('Failed to generate Duration Excel');
        }
    };

    const exportDurationTableToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const title = "Tickets By Duration (Open Tickets)";

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['Employee Name', '< 5 Days', '5 - 10 Days', 'Critical (>10d)', 'Total Open']],
                body: filteredDurationTable.map(row => [
                    row.employee_name,
                    row.bucket_less_5 || 0,
                    row.bucket_5_10 || 0,
                    row.bucket_more_10 || 0,
                    Number(row.bucket_less_5 || 0) + Number(row.bucket_5_10 || 0) + Number(row.bucket_more_10 || 0)
                ]),
                foot: [['Total', durationTotals.bucket_less_5, durationTotals.bucket_5_10, durationTotals.bucket_more_10, (durationTotals.bucket_less_5 + durationTotals.bucket_5_10 + durationTotals.bucket_more_10)]],
                theme: 'grid',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontSize: 10,
                    fontStyle: 'bold',
                    halign: 'center',
                    cellPadding: 8
                },
                footStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [20, 20, 20],
                    fontSize: 10,
                    fontStyle: 'bold',
                    halign: 'right'
                },
                styles: { fontSize: 9, cellPadding: 6, textColor: [40, 40, 40] },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Company Name
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });

                    // Report Title
                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    // Date
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    // Separator
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);

                    // Footer
                    doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(`Report Date: ${now}`, 32, pageHeight - 22);
                    doc.text(`Page ${data.pageNumber} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            doc.save(`Tickets_Duration_Report_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('Duration PDF Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate Duration PDF');
        }
    };
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i);

    const handlePrevMonth = () => {
        if (selectedMonth === 'all') {
            setSelectedYear(prev => prev - 1);
            return;
        }
        if (selectedMonth === 0) {
            setSelectedMonth(11);
            setSelectedYear(prev => prev - 1);
        } else {
            setSelectedMonth(prev => prev - 1);
        }
    };

    const handleNextMonth = () => {
        if (selectedMonth === 'all') {
            setSelectedYear(prev => prev + 1);
            return;
        }
        if (selectedMonth === 11) {
            setSelectedMonth(0);
            setSelectedYear(prev => prev + 1);
        } else {
            setSelectedMonth(prev => prev + 1);
        }
    };

    const fetchAdditionalTrends = useCallback(async () => {
        try {
            setTrendsLoading(true);
            const trendStartStr = newTrendsSelectedYear === 'all'
                ? '2020-01-01'
                : (newTrendsSelectedMonth === 'all' ? format(new Date(newTrendsSelectedYear, 0, 1), 'yyyy-MM-dd') : format(new Date(newTrendsSelectedYear, newTrendsSelectedMonth, 1), 'yyyy-MM-dd'));
            const trendEndStr = newTrendsSelectedYear === 'all'
                ? format(new Date(), 'yyyy-MM-dd')
                : (newTrendsSelectedMonth === 'all' ? format(new Date(newTrendsSelectedYear, 11, 31), 'yyyy-MM-dd') : format(new Date(newTrendsSelectedYear, newTrendsSelectedMonth + 1, 0), 'yyyy-MM-dd'));

            const trendsRes = await api.get('ticket/getAnalyticsData', {
                params: {
                    fromDate: trendStartStr,
                    toDate: trendEndStr,
                    _cb: Date.now()
                }
            });
            if (trendsRes.data.status) {
                setRepeatTrends(trendsRes.data.repeat_trends || []);
                setTestingRejectTrends(trendsRes.data.testing_reject_trends || []);
            }
        } catch (err) {
            console.error("Error fetching additional trends:", err);
        } finally {
            setTrendsLoading(false);
        }
    }, [newTrendsSelectedMonth, newTrendsSelectedYear]);

    /**
     * Drilldown handler for Repeat + Testing Reject bar charts.
     * Fetches from ticket/getAnalyticsDrilldown, normalises fields to the
     * exact shape the existing modal table + donut charts expect.
     */
    const fetchRepeatRejectDrilldown = useCallback(async (month, title, type) => {
        setModalPage(1);
        setModalSearch('');
        setModalAssigneeFilter(null);
        setShowDrilldownChart(false);
        setDrilldownChartHeight(380);
        setModalConfig(prev => ({ ...prev, show: true, loading: true, tickets: [], chartData: [], title }));

        try {
            const res = await api.get('ticket/getAnalyticsDrilldown', {
                params: { type, month }
            });

            const raw = res.data?.data || res.data?.tickets || [];

            // Normalise each ticket to match the existing modal's field names
            const tickets = raw.map(t => {
                const currently_working = type === 'reject' || type === 'repeat'
                    ? (t.assignee_name || t.currently_working || t.employee_name || 'N/A')
                    : (t.employee_name || t.assignee_name || t.currently_working || 'N/A');

                const assign_id = type === 'reject' || type === 'repeat'
                    ? (t.dev_id || t.assign_id || t.user_id || 0)
                    : (t.assign_id || t.user_id || 0);

                return {
                    ...t,
                    // 'currently_working' is what the modal table renders for Assigned To
                    currently_working,
                    // 'collaborators' (string) is what the modal table renders for All Collaborators
                    collaborators: Array.isArray(t.employees)
                        ? t.employees.map(e =>
                            ((e.first_name || '') + ' ' + (e.last_name || '')).trim() || e.user_name || `User #${e.user_id}`
                        ).join(', ')
                        : (t.collaborators || 'N/A'),
                    // 'origin' from customer_name (Internal / External)
                    origin: t.origin || 'External',
                    // 'aging_display' — use total_days if available
                    aging_display: t.aging_display || (t.total_days && t.total_days !== '-' ? `${t.total_days}d` : '—'),
                    // assign_id for donut chart filtering
                    assign_id,
                };
            });

            // Build chartData (assignee distribution) exactly like LeadtrendsModel returns
            const summary = {};
            tickets.forEach(t => {
                const name = t.currently_working || 'Unassigned';
                const uid = t.assign_id || 0;
                if (!summary[name]) summary[name] = { name, id: uid, y: 0 };
                summary[name].y++;
            });
            const chartData = Object.values(summary).sort((a, b) => b.y - a.y);

            setModalConfig(prev => ({
                ...prev,
                loading: false,
                tickets,
                chartData,
                title,
                type,
            }));
        } catch (err) {
            console.error('Repeat/Reject drilldown failed:', err);
            setModalConfig(prev => ({ ...prev, loading: false }));
            toast.error('Unable to fetch drilldown data');
        }
    }, []);

    useEffect(() => {
        fetchAdditionalTrends();
    }, [fetchAdditionalTrends]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const startStr = selectedYear === 'all'
                ? '2020-01-01'
                : (selectedMonth === 'all' ? format(new Date(selectedYear, 0, 1), 'yyyy-MM-dd') : format(new Date(selectedYear, selectedMonth, 1), 'yyyy-MM-dd'));
            const endStr = selectedYear === 'all'
                ? format(new Date(), 'yyyy-MM-dd')
                : (selectedMonth === 'all' ? format(new Date(selectedYear, 11, 31), 'yyyy-MM-dd') : format(new Date(selectedYear, selectedMonth + 1, 0), 'yyyy-MM-dd'));

            const trendStartStr = trendSelectedYear === 'all'
                ? '2020-01-01'
                : (trendSelectedMonth === 'all' ? format(new Date(trendSelectedYear, 0, 1), 'yyyy-MM-dd') : format(new Date(trendSelectedYear, trendSelectedMonth, 1), 'yyyy-MM-dd'));
            const trendEndStr = trendSelectedYear === 'all'
                ? format(new Date(), 'yyyy-MM-dd')
                : (trendSelectedMonth === 'all' ? format(new Date(trendSelectedYear, 11, 31), 'yyyy-MM-dd') : format(new Date(trendSelectedYear, trendSelectedMonth + 1, 0), 'yyyy-MM-dd'));

            const response = await api.get('charts/ticketchart/lead-trends', {
                params: {
                    date_from: startStr,
                    date_to: endStr,
                    trend_date_from: trendStartStr,
                    trend_date_to: trendEndStr,
                    _cb: Date.now()
                }
            });
            setData(response.data);

            const delayStartStr = delaySelectedYear === 'all'
                ? '2020-01-01'
                : (delaySelectedMonth === 'all' ? format(new Date(delaySelectedYear, 0, 1), 'yyyy-MM-dd') : format(new Date(delaySelectedYear, delaySelectedMonth, 1), 'yyyy-MM-dd'));
            const delayEndStr = delaySelectedYear === 'all'
                ? format(new Date(), 'yyyy-MM-dd')
                : (delaySelectedMonth === 'all' ? format(new Date(delaySelectedYear, 11, 31), 'yyyy-MM-dd') : format(new Date(delaySelectedYear, delaySelectedMonth + 1, 0), 'yyyy-MM-dd'));

            setDelayLoading(true);
            const delayResponse = await api.get('charts/ticketchart/hrms-overview-charts-data', {
                params: {
                    date_from: delayStartStr,
                    date_to: delayEndStr,
                    _cb: Date.now()
                }
            });
            if (delayResponse.data.status) {
                setDelayAnalytics(delayResponse.data.data);
            }
        } catch (error) {
            console.error("Error fetching analytics data:", error);
        } finally {
            setLoading(false);
            setDelayLoading(false);
        }
    }, [selectedMonth, selectedYear, trendSelectedMonth, trendSelectedYear, delaySelectedMonth, delaySelectedYear]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const exportRepeatToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Repeat Trends');
            worksheet.addRow(['Month', 'Company Total Reopens']);
            repeatTrends.forEach(d => worksheet.addRow([d.month, d.company_reopens]));
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Repeat_Trends_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            a.click();
            toast.success('Excel Exported');
        } catch (err) { toast.error('Export failed'); }
    };

    const exportRejectToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Reject Trends');
            worksheet.addRow(['Month', 'Reject Count']);
            testingRejectTrends.forEach(d => worksheet.addRow([d.month, d.reject_count]));
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Reject_Trends_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            a.click();
            toast.success('Excel Exported');
        } catch (err) { toast.error('Export failed'); }
    };

    const exportTrendsToPDF = (type) => {
        try {
            const doc = new jsPDF();
            const title = type === 'repeat' ? 'Repeat Ticket Trends' : 'Testing Reject Status Trends';
            const data = type === 'repeat' ? repeatTrends : testingRejectTrends;
            const headers = type === 'repeat' ? [['Month', 'Company Reopens']] : [['Month', 'Reject Count']];
            const body = type === 'repeat' ? data.map(d => [d.month, d.company_reopens]) : data.map(d => [d.month, d.reject_count]);

            doc.text(title, 10, 10);
            autoTable(doc, { head: headers, body: body });
            doc.save(`${type}_trends_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('PDF Exported');
        } catch (err) { toast.error('Export failed'); }
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };
    const sortedEmployeeData = useMemo(() => {
        let sortableData = [...(data.employeeStatusTable || [])];
        if (searchTerm) {
            sortableData = sortableData.filter(item =>
                item.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                const aVal = a[sortConfig.key] ?? 0;
                const bVal = b[sortConfig.key] ?? 0;
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableData;
    }, [data.employeeStatusTable, sortConfig, searchTerm]);

    const filteredStatuses = useMemo(() => {
        // We trust the backend (data.statuses) to have filtered the columns correctly for the current mode
        return (data.statuses || []);
    }, [data.statuses]);

    const getStatusValue = (row, statusId, statusName) => Number(row?.[`status_${statusId}`] ?? row?.[statusName] ?? 0);

    const getPendingCustValue = (row) => {
        if (!row) return 0;
        let val = Number(row['Pending Customer Verification & Confirmation'] ?? row['Pending(Cust)'] ?? 0);
        if (!isNaN(val) && val !== 0) return val;
        for (let key in row) {
            if (typeof key === 'string' && key.toLowerCase().includes('pending') && key.toLowerCase().includes('cust')) {
                return Number(row[key] || 0);
            }
        }
        return 0;
    };

    const employeeTotals = useMemo(() => {
        const totals = { total: 0, external: 0, internal: 0, pendingCust: 0 };
        filteredStatuses.forEach(s => { totals[`status_${s.status_id}`] = 0; });
        (data.employeeStatusTable || []).forEach(row => {
            totals.total += Number(row.total || 0);
            totals.external += Number(row.external || 0);
            totals.internal += Number(row.internal || 0);
            totals.pendingCust += getPendingCustValue(row);
            filteredStatuses.forEach(s => {
                totals[`status_${s.status_id}`] += getStatusValue(row, s.status_id, s.status_name);
            });
        });
        return totals;
    }, [data.employeeStatusTable, filteredStatuses]);

    const durationTotals = useMemo(() => {
        return (data.durationTable || []).reduce((acc, current) => ({
            bucket_less_5: acc.bucket_less_5 + Number(current.bucket_less_5 || 0),
            bucket_5_10: acc.bucket_5_10 + Number(current.bucket_5_10 || 0),
            bucket_more_10: acc.bucket_more_10 + Number(current.bucket_more_10 || 0),
        }), { bucket_less_5: 0, bucket_5_10: 0, bucket_more_10: 0 });
    }, [data.durationTable]);

    const filteredDurationTable = useMemo(() => {
        return (data.durationTable || []).filter(row =>
            (Number(row.bucket_less_5 || 0) + Number(row.bucket_5_10 || 0) + Number(row.bucket_more_10 || 0)) > 0
        );
    }, [data.durationTable]);

    const exportAgingToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Aging Analytics');
            const columns = ['S.No', 'Aging Bucket', 'Ticket Count'];
            const totalCols = columns.length;
            const colRange = 'C';

            // Brand Header
            worksheet.mergeCells('A1:C1');
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Report Title
            const title = "Ticket Delay by Creation Date";
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells('A2:C2');
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Table Header
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            headerRow.height = 25;

            let totalCount = 0;
            delayAnalytics.aging.forEach((item, rowIndex) => {
                totalCount += item.count;
                const excelRow = worksheet.addRow([rowIndex + 1, item.bucket, item.count]);
                excelRow.eachCell((cell) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    if (rowIndex % 2 === 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                });
                excelRow.height = 22;
            });

            const footerRow = worksheet.addRow(['Total', '', totalCount]);
            footerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
            footerRow.height = 25;

            worksheet.getColumn(1).width = 10;
            worksheet.getColumn(2).width = 30;
            worksheet.getColumn(3).width = 15;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Aging_Analytics_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            a.click();
            toast.success('Aging Excel Downloaded');
        } catch (error) { toast.error('Aging Export Failed'); }
    };

    const exportAgingToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const title = "Ticket Delay by Creation Date";

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['Aging Bucket', 'Ticket Count']],
                body: delayAnalytics.aging.map(item => [item.bucket, item.count]),
                theme: 'grid',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'center',
                    cellPadding: 10
                },
                styles: { fontSize: 10, cellPadding: 8 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Company Name
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });

                    // Report Title
                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    // Date
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    // Separator
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);

                    // Footer
                    doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(`Report Date: ${now}`, 32, pageHeight - 22);
                    doc.text(`Page ${data.pageNumber} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            doc.save(`Aging_Analytics_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('Aging PDF Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const exportWaitingToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Waiting Bottlenecks');
            const columns = ['S.No', 'Status', 'Ticket Count'];
            const totalCols = columns.length;
            const colRange = 'C';

            // Brand Header
            worksheet.mergeCells('A1:C1');
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Report Title
            const title = "Ticket Delay Waiting On Someone";
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells('A2:C2');
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Table Header
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
            headerRow.height = 25;

            let totalCount = 0;
            delayAnalytics.waiting.forEach((item, rowIndex) => {
                totalCount += item.count;
                const excelRow = worksheet.addRow([rowIndex + 1, item.status, item.count]);
                excelRow.eachCell((cell) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    if (rowIndex % 2 === 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                });
                excelRow.height = 22;
            });

            const footerRow = worksheet.addRow(['Total', '', totalCount]);
            footerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
            footerRow.height = 25;

            worksheet.getColumn(1).width = 10;
            worksheet.getColumn(2).width = 30;
            worksheet.getColumn(3).width = 15;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Waiting_Bottlenecks_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            a.click();
            toast.success('Waiting Excel Downloaded');
        } catch (error) { toast.error('Waiting Export Failed'); }
    };

    const exportWaitingToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const title = "Ticket Delay Waiting On Someone";

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['Status', 'Ticket Count']],
                body: delayAnalytics.waiting.map(item => [item.status, item.count]),
                theme: 'grid',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'center',
                    cellPadding: 10
                },
                styles: { fontSize: 10, cellPadding: 8 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Company Name
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });

                    // Report Title
                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    // Date
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    // Separator
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);

                    // Footer
                    doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(`Report Date: ${now}`, 32, pageHeight - 22);
                    doc.text(`Page ${data.pageNumber} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });
                }
            });

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages("{total_pages_count_string}");
            }

            doc.save(`Waiting_Bottlenecks_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('Waiting Bottlenecks PDF Downloaded');
        } catch (error) {
            console.error('PDF error:', error);
            toast.error('Failed to generate PDF');
        }
    };

    const agingOptions = {
        chart: { type: 'column', backgroundColor: '#FFFFFF', height: 280 },
        title: { text: null },
        xAxis: { categories: (delayAnalytics.aging || []).map(item => item.bucket), labels: { style: { fontSize: '10px', fontWeight: '800', color: '#64748b' } } },
        yAxis: { title: { text: null }, gridLineColor: '#f1f5f9', labels: { style: { fontSize: '10px', color: '#94a3b8' } } },
        plotOptions: {
            column: {
                borderRadius: 6,
                colorByPoint: true,
                colors: COLORS,
                cursor: 'pointer',
                point: { events: { click: function () { fetchTicketDetails(null, 'Overall', null, this.category); } } },
                dataLabels: { enabled: true, style: { fontSize: '10px', fontWeight: '900', color: '#475569' } }
            }
        },
        series: [{ name: 'Tickets', data: (delayAnalytics.aging || []).map(item => parseInt(item.count)), showInLegend: false }],
        credits: { enabled: false },
        tooltip: { backgroundColor: '#ffffff', borderRadius: 12, borderWidth: 0, shadow: true, style: { fontSize: '11px' } }
    };

    const waitingOptions = {
        chart: { type: 'pie', backgroundColor: '#FFFFFF', height: 280 },
        title: { text: null },
        tooltip: { pointFormat: '<b>{point.name}</b>: {point.y} Tickets', backgroundColor: '#ffffff', borderRadius: 12, borderWidth: 0, shadow: true },
        plotOptions: {
            pie: {
                innerSize: '65%',
                colors: ['#8B5CF6', '#F59E0B', '#10B981', '#EC4899'],
                dataLabels: { enabled: true, format: '{point.name}: {point.y}', style: { fontSize: '10px', fontWeight: '800', color: '#64748b' }, connectorColor: '#e2e8f0' },
                cursor: 'pointer',
                point: { events: { click: function () { fetchTicketDetails(null, 'Overall', this.name); } } }
            }
        },
        series: [{ name: 'Tickets', colorByPoint: true, data: (delayAnalytics.waiting || []).map(item => ({ name: item.status, y: parseInt(item.count) })) }],
        credits: { enabled: false }
    };

    const employeePieData = useMemo(() => {
        return (data.employeeStatusTable || [])
            .map(emp => ({ name: emp.employee_name, y: Number(emp.total || 0) }))
            .filter(item => item.y > 0);
    }, [data.employeeStatusTable]);

    const trendData = useMemo(() => {
        const trend = data.newTicketsTrend?.data || [];
        const grandTotal = data.newTicketsTrend?.grandTotal || 1;
        return trend.map(item => ({
            customer: item.customer,
            shortCode: item.short_code || item.customer,
            custKey: item.cust_key,
            newCount: Number(item.new_count),
            totalCount: Number(item.total_count),
            percentage: ((Number(item.new_count) / grandTotal) * 100).toFixed(1)
        })).filter(d => d.newCount > 0);
    }, [data.newTicketsTrend]);

    const trendChartOptions = useMemo(() => ({
        chart: {
            type: 'column',
            backgroundColor: '#FFFFFF',
            height: isFullscreen ? 600 : 450,
            spacingTop: 40,
            zooming: { type: 'xy' }
        },
        title: {
            text: 'New Tickets Trend – Customer-wise',
            align: 'left',
            style: { fontWeight: '900', fontSize: '18px', color: '#1e293b', letterSpacing: '-0.5px' }
        },
        subtitle: {
            text: 'Distribution of newly created tickets across customers',
            align: 'left',
            style: { color: '#64748b', fontSize: '12px' }
        },
        xAxis: {
            categories: trendData.map(d => d.customer),
            crosshair: true,
            labels: {
                style: { color: '#94a3b8', fontWeight: 'bold' },
                formatter: function () {
                    const d = trendData.find(x => x.customer === this.value);
                    if (this.chart.chartWidth < 600) {
                        return d ? d.shortCode : this.value;
                    }
                    return this.value;
                }
            },
            lineColor: '#f1f5f9'
        },
        yAxis: {
            title: { text: 'Tickets Count', style: { color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '10px', fontWeight: '800' } },
            gridLineColor: '#f1f5f9',
            labels: { style: { color: '#94a3b8' } }
        },
        tooltip: {
            shared: true,
            useHTML: true,
            headerFormat: '<span style="font-size: 10px; font-weight: 800; color: #64748b; text-transform: uppercase;">{point.key}</span><br>',
            pointFormatter: function () {
                const d = trendData.find(x => x.customer === this.category);
                return `<div style="display: flex; align-items: center; gap: 8px; margin-top: 5px;">
                            <div style="width: 8px; height: 8px; border-radius: 50%; background: ${this.color}"></div>
                            <span style="font-weight: 800; color: #1e293b;">${this.series.name}:</span>
                            <span style="font-weight: 900; color: ${this.color}">${this.y}</span>
                            ${this.series.name === 'New Tickets' ? `<span style="font-size: 11px; color: #64748b; margin-left: 5px;">(${d.percentage}%)</span>` : ''}
                        </div>`;
            }
        },
        plotOptions: {
            series: {
                borderRadius: 8,
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const d = trendData.find(x => x.customer === this.category);
                            if (d) {
                                fetchTicketDetails(null, d.customer, 'New', null, d.custKey);
                            }
                        }
                    }
                },
                states: { hover: { brightness: 0.1, halo: { size: 10, opacity: 0.15 } } }
            },
            column: {
                groupPadding: 0.2,
                pointPadding: 0.05,
                borderWidth: 0,
                color: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                        [0, '#3b82f6'],
                        [1, 'rgba(59, 130, 246, 0.05)']
                    ]
                }
            },
            areaspline: {
                fillColor: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                        [0, 'rgba(16, 185, 129, 0.3)'],
                        [1, 'rgba(16, 185, 129, 0)']
                    ]
                },
                marker: { enabled: false },
                lineWidth: 3,
                lineColor: '#10b981',
                states: { hover: { lineWidthPlus: 1 } }
            }
        },
        series: [
            {
                name: 'Trend Overview',
                type: 'areaspline',
                data: trendData.map(d => d.totalCount),
                zIndex: 0,
                opacity: 0.2
            },
            {
                name: 'New Tickets',
                type: 'column',
                data: trendData.map(d => d.newCount),
                zIndex: 1
            }
        ],
        exporting: {
            buttons: {
                contextButton: {
                    menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG']
                }
            }
        },
        credits: { enabled: false }
    }), [trendData, isFullscreen]);

    const pieChartOptions = useMemo(() => ({
        chart: { type: 'pie', backgroundColor: '#FFFFFF', height: 380 },
        title: { text: null },
        tooltip: { pointFormat: '{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)' },
        plotOptions: {
            pie: {
                innerSize: '55%',
                colors: COLORS,
                dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}', style: { fontSize: '10px', fontWeight: '800' } },
                showInLegend: true
            }
        },
        legend: { itemStyle: { fontSize: '10px', color: '#64748b' }, labelFormatter: function () { return `${this.name} (${this.y})`; } },
        series: [{ name: 'Tickets', colorByPoint: true, data: employeePieData }],
        credits: { enabled: false }
    }), [employeePieData]);

    if (loading && data.statuses.length === 0) {
        return <div className="flex items-center justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;
    }

    const cardStyle = "bg-white p-6 rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-gray-50 flex flex-col";

    return (
        <div className="w-full bg-[#f8fafc] py-8 px-4 sm:px-6">
            <div className="max-w-[1700px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 pb-4 border-b border-slate-100">
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                            <div className="w-1.5 h-8 bg-red-600 rounded-full"></div>
                            Department Ticket Trends
                        </h2>
                        <p className="text-[10px] font-black text-slate-400 mt-1 uppercase tracking-[0.2em] flex items-center gap-2">
                            <FiActivity className="text-red-500" /> Lead and Head Performance Analytics (Live Sync)
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-8">
                    <div className={`${cardStyle} flex-col group`}>
                        <div className="relative z-10 flex flex-wrap justify-end items-center gap-2 mb-2 w-full">
                            <div className="flex items-center gap-2 mr-2">
                                <div className="relative group">
                                    <select
                                        value={trendSelectedMonth}
                                        onChange={(e) => setTrendSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[100px] outline-none"
                                    >
                                        <option value="all">All Months</option>
                                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="relative group">
                                    <select
                                        value={trendSelectedYear}
                                        onChange={(e) => setTrendSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[80px] outline-none"
                                    >
                                        <option value="all">All Year</option>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                            </div>
                            <div className="flex items-center gap-3 bg-white px-3 py-1.5 rounded-full shadow-[0_2px_12px_rgba(0,0,0,0.06)] border border-slate-100/60 transition-all">
                                <button
                                    onClick={exportTrendToExcel}
                                    className="text-emerald-500 hover:text-emerald-600 hover:scale-110 active:scale-95 transition-all"
                                    title="Export Excel"
                                >
                                    <FiDownload size={16} strokeWidth={2.5} />
                                </button>
                                <button
                                    onClick={exportTrendToPDF}
                                    className="text-red-500 hover:text-red-600 hover:scale-110 active:scale-95 transition-all"
                                    title="Export PDF"
                                >
                                    <FiFileText size={16} strokeWidth={2.5} />
                                </button>
                            </div>
                            <button
                                onClick={() => setIsFullscreen(true)}
                                className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-400 rounded-lg transition-all"
                                title="Full Screen"
                            >
                                <FiMaximize2 size={16} />
                            </button>
                        </div>
                        <div className="flex-1 min-h-[450px]">
                            <HighchartsReact highcharts={Highcharts} options={trendChartOptions} />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 pb-8">
                    <div className={`${cardStyle} xl:col-span-4`}>
                        <h4 className="text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2 mb-6"><FiBarChart2 className="text-emerald-500" /> Ticket By Status - Chart</h4>
                        <div className="flex-1 min-h-[400px] flex items-center justify-center">
                            {employeePieData.length > 0 ? (
                                <div className="w-full">
                                    <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />
                                </div>
                            ) : (
                                <div className="text-slate-300 text-center py-20">
                                    <FiClock className="text-5xl mx-auto mb-2 opacity-20" />
                                    <p className="text-xs font-black uppercase tracking-widest">No active tickets</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`${cardStyle} xl:col-span-8 !p-0 overflow-hidden flex flex-col h-full`}>
                        <div className="p-4 border-b border-gray-50 flex flex-col xl:flex-row items-center justify-between bg-white gap-4 shrink-0">
                            <div className="flex flex-col sm:flex-row items-center gap-4 w-full xl:w-auto">
                                <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest whitespace-nowrap">Ticket By Status</h4>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="relative group">
                                            <select
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                                className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[100px] outline-none"
                                            >
                                                <option value="all">All Months</option>
                                                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                            </select>
                                            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                        </div>

                                        <div className="relative group">
                                            <select
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                                className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[80px] outline-none"
                                            >
                                                <option value="all">All Year</option>
                                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                        <button
                                            disabled={selectedYear === 'all'}
                                            onClick={handlePrevMonth}
                                            className="w-9 h-9 flex items-center justify-center text-slate-500 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:text-red-500 hover:bg-red-50/30 transition-all shadow-sm active:scale-95 disabled:opacity-20 disabled:cursor-not-allowed"
                                        >
                                            <FiChevronLeft size={16} />
                                        </button>
                                        <button
                                            disabled={selectedYear === 'all'}
                                            onClick={handleNextMonth}
                                            className="w-9 h-9 flex items-center justify-center text-slate-500 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:text-red-500 hover:bg-red-50/30 transition-all shadow-sm active:scale-95 disabled:opacity-20 disabled:cursor-not-allowed"
                                        >
                                            <FiChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 w-full xl:w-auto">
                                <div className="relative w-full xl:w-auto">
                                    <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                                    <input
                                        type="text"
                                        placeholder="Filter employee..."
                                        className="pl-9 pr-4 py-1.5 text-xs font-semibold bg-slate-50/50 border border-slate-100 rounded-xl w-full xl:w-64 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-500/10 transition-all shadow-sm"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center gap-3 bg-white px-3 py-1.5 rounded-full shadow-[0_2px_12px_rgba(0,0,0,0.06)] border border-slate-100/60">
                                    <button
                                        onClick={exportStatusTableToExcel}
                                        className="text-emerald-500 hover:text-emerald-600 hover:scale-110 active:scale-95 transition-all"
                                        title="Export Excel"
                                    >
                                        <FiDownload size={16} strokeWidth={2.5} />
                                    </button>
                                    <button
                                        onClick={exportStatusTableToPDF}
                                        className="text-red-500 hover:text-red-600 hover:scale-110 active:scale-95 transition-all"
                                        title="Export PDF"
                                    >
                                        <FiFileText size={16} strokeWidth={2.5} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto custom-scrollbar flex-1 relative" style={{ minHeight: '300px', maxHeight: '480px' }}>
                            <table className="w-full h-full text-left border-collapse min-w-[700px]">
                                <thead className="sticky top-0 bg-slate-50/95 backdrop-blur-md z-30">
                                    <tr className="border-b border-slate-100">
                                        <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] cursor-pointer" onClick={() => handleSort('employee_name')}>
                                            Employee {sortConfig.key === 'employee_name' && (sortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                        </th>
                                        <th className="px-6 py-4 text-[10px] font-black text-center text-red-600 bg-red-50/40 uppercase tracking-[0.1em] border-r border-red-50">Opened</th>
                                        <th className="px-3 py-4 text-[10px] font-black text-center text-blue-600 bg-blue-50/20 uppercase tracking-[0.1em] border-r border-blue-50/30">External</th>
                                        <th className="px-3 py-4 text-[10px] font-black text-center text-indigo-600 bg-indigo-50/20 uppercase tracking-[0.1em] border-r border-indigo-50/30">Internal</th>
                                        {filteredStatuses.map(s => <th key={s.status_id} className="px-3 py-4 text-[10px] font-black text-center text-slate-500 uppercase tracking-[0.1em]">{s.status_name}</th>)}
                                        <th className="px-3 py-4 text-[10px] font-black text-center text-slate-500 uppercase tracking-[0.1em]">Pending Customer Verification & Confirmation</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {sortedEmployeeData.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-blue-50/30 transition-colors group">
                                            <td className="px-6 py-4 text-[12px] font-bold text-slate-700 group-hover:text-emerald-700">{row.employee_name}</td>
                                            <td className="px-6 py-4 text-[12px] font-black text-red-600 text-center bg-red-50/20 border-r border-red-50 cursor-pointer underline decoration-red-200 hover:bg-red-50 transition-all font-mono" onClick={() => fetchTicketDetails(row.user_id, row.employee_name)}>
                                                {row.total || 0}
                                            </td>
                                            <td className="px-3 py-4 text-[12px] font-black text-blue-600 text-center bg-blue-50/10 border-r border-blue-50/20 cursor-pointer hover:bg-blue-50 transition-all" onClick={() => row.external > 0 && fetchTicketDetails(row.user_id, row.employee_name, null, null, null, 'external')}>
                                                {row.external || 0}
                                            </td>
                                            <td className="px-3 py-4 text-[12px] font-black text-indigo-600 text-center bg-indigo-50/10 border-r border-indigo-50/20 cursor-pointer hover:bg-indigo-50 transition-all" onClick={() => row.internal > 0 && fetchTicketDetails(row.user_id, row.employee_name, null, null, null, 'internal')}>
                                                {row.internal || 0}
                                            </td>
                                            {filteredStatuses.map(s => {
                                                const value = getStatusValue(row, s.status_id, s.status_name);
                                                return (
                                                    <td key={s.status_id} className="px-3 py-4 text-[12px] text-slate-500 text-center font-semibold cursor-pointer hover:bg-slate-100 transition-all" onClick={() => value > 0 && fetchTicketDetails(row.user_id, row.employee_name, s.status_name)}>
                                                        {value}
                                                    </td>
                                                );
                                            })}
                                            <td className="px-3 py-4 text-[12px] text-slate-500 text-center font-semibold cursor-pointer hover:bg-slate-100 transition-all" onClick={() => getPendingCustValue(row) > 0 && fetchTicketDetails(row.user_id, row.employee_name, 'Pending Customer Verification & Confirmation')}>
                                                {getPendingCustValue(row)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-100 text-slate-700 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                                    <tr className="divide-x divide-slate-100">
                                        <td className="px-6 py-4 text-[11px] font-black uppercase tracking-widest">Total</td>
                                        <td className="px-6 py-4 text-[11px] font-black text-center text-red-600 bg-red-50/30">{employeeTotals.total}</td>
                                        <td className="px-3 py-4 text-[11px] font-black text-center text-blue-600 bg-blue-50/10">{employeeTotals.external}</td>
                                        <td className="px-3 py-4 text-[11px] font-black text-center text-indigo-600 bg-indigo-50/10">{employeeTotals.internal}</td>
                                        {filteredStatuses.map(s => <td key={s.status_id} className="px-3 py-4 text-[11px] font-black text-center">{employeeTotals[`status_${s.status_id}`] || 0}</td>)}
                                        <td className="px-3 py-4 text-[11px] font-black text-center">{employeeTotals.pendingCust}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    <div className={`${cardStyle} !p-0 overflow-hidden`}>
                        <div className="p-4 border-b border-gray-50 flex items-center justify-between bg-white">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest">Tickets By Duration(Open Tickets)</h4>
                            <div className="flex items-center gap-3 bg-white px-3 py-1.5 rounded-full shadow-[0_2px_12px_rgba(0,0,0,0.06)] border border-slate-100/60 transition-all">
                                <button
                                    onClick={exportDurationTableToExcel}
                                    className="text-emerald-500 hover:text-emerald-600 hover:scale-110 active:scale-95 transition-all"
                                    title="Export Excel"
                                >
                                    <FiDownload size={16} strokeWidth={2.5} />
                                </button>
                                <button
                                    onClick={exportDurationTableToPDF}
                                    className="text-red-500 hover:text-red-600 hover:scale-110 active:scale-95 transition-all"
                                    title="Export PDF"
                                >
                                    <FiFileText size={16} strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>
                        <div className="overflow-auto custom-scrollbar" style={{ maxHeight: '480px' }}>
                            <table className="w-full text-left border-collapse">
                                <thead className="sticky top-0 bg-slate-50/95 backdrop-blur-md z-30 border-b border-slate-100">
                                    <tr>
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Employee</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-center text-slate-500 uppercase tracking-widest">&lt; 5 Days</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-center text-slate-500 uppercase tracking-widest">5 - 10 Days</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-center text-red-500 tracking-widest uppercase">Critical (&gt;10d)</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-600 bg-slate-100/50 text-center border-l border-slate-100">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredDurationTable.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-8 py-5 text-[12px] font-bold text-slate-700">{row.employee_name}</td>
                                            <td className="px-8 py-5 text-[12px] text-blue-500 text-center font-bold cursor-pointer hover:bg-blue-50/50 underline decoration-blue-200" onClick={() => Number(row.bucket_less_5) > 0 && fetchTicketDetails(row.user_id, row.employee_name, null, 'less_5')}>{row.bucket_less_5 || 0}</td>
                                            <td className="px-8 py-5 text-[12px] text-orange-500 text-center font-bold cursor-pointer hover:bg-orange-50/50 underline decoration-orange-200" onClick={() => Number(row.bucket_5_10) > 0 && fetchTicketDetails(row.user_id, row.employee_name, null, '5_10')}>{row.bucket_5_10 || 0}</td>
                                            <td className={`px-8 py-5 text-[12px] text-center font-black cursor-pointer underline ${Number(row.bucket_more_10) > 0 ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'text-slate-300'}`} onClick={() => Number(row.bucket_more_10) > 0 && fetchTicketDetails(row.user_id, row.employee_name, null, 'more_10')}>{row.bucket_more_10 || 0}</td>
                                            <td className="px-8 py-5 text-[12px] font-black text-slate-800 text-center bg-slate-50/30 border-l border-slate-100 cursor-pointer" onClick={() => fetchTicketDetails(row.user_id, row.employee_name)}>{Number(row.bucket_less_5 || 0) + Number(row.bucket_5_10 || 0) + Number(row.bucket_more_10 || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t-2 border-slate-100 text-slate-700 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                                    <tr className="divide-x divide-slate-100">
                                        <td className="px-8 py-4 text-[11px] font-black uppercase tracking-widest">Total Open Tikcets</td>
                                        <td className="px-8 py-4 text-[11px] font-black text-center">{durationTotals.bucket_less_5}</td>
                                        <td className="px-8 py-4 text-[11px] font-black text-center">{durationTotals.bucket_5_10}</td>
                                        <td className="px-8 py-4 text-[11px] font-black text-center text-red-600 bg-red-50/30">{durationTotals.bucket_more_10}</td>
                                        <td className="px-8 py-4 text-[11px] font-black text-center bg-slate-100/50">{durationTotals.bucket_less_5 + durationTotals.bucket_5_10 + durationTotals.bucket_more_10}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-10 mt-10">
                    {/* Ticket Delay By Creation Date Card */}
                    <div className={`${cardStyle} h-full !p-0 overflow-hidden`}>
                        <div className="p-4 border-b border-gray-50 flex flex-col sm:flex-row items-center justify-between bg-white gap-4">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                                <FiClock className="text-blue-500" /> Ticket Delay by Creation Date
                            </h4>
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <select
                                        value={delaySelectedMonth}
                                        onChange={(e) => setDelaySelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[105px]"
                                    >
                                        <option value="all">All Month</option>
                                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="relative group">
                                    <select
                                        value={delaySelectedYear}
                                        onChange={(e) => setDelaySelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[85px]"
                                    >
                                        <option value="all">All Year</option>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                                    <button onClick={exportAgingToExcel} className="text-emerald-500 hover:scale-110 transition-all" title="Excel"><FiDownload size={14} /></button>
                                    <button onClick={exportAgingToPDF} className="text-red-500 hover:scale-110 transition-all" title="PDF"><FiFileText size={14} /></button>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 relative min-h-[300px]">
                            {delayLoading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px] z-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
                            ) : delayAnalytics.aging.length > 0 ? (
                                <HighchartsReact highcharts={Highcharts} options={agingOptions} />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 text-slate-300">
                                    <FiBarChart2 size={40} className="mb-2 opacity-10" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">No aging data available</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Summary</span>
                            <span className="text-[11px] font-black text-slate-700 uppercase">Total Tickets: {delayAnalytics.aging.reduce((sum, i) => sum + parseInt(i.count), 0)}</span>
                        </div>
                    </div>

                    {/* Ticket Delay Waiting On Someone Card */}
                    <div className={`${cardStyle} h-full !p-0 overflow-hidden`}>
                        <div className="p-4 border-b border-gray-50 flex flex-col sm:flex-row items-center justify-between bg-white gap-4">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                                <FiActivity className="text-purple-500" /> Ticket Delay Waiting On Someone
                            </h4>
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <select
                                        value={delaySelectedMonth}
                                        onChange={(e) => setDelaySelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[105px]"
                                    >
                                        <option value="all">All Month</option>
                                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="relative group">
                                    <select
                                        value={delaySelectedYear}
                                        onChange={(e) => setDelaySelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[85px]"
                                    >
                                        <option value="all">All Year</option>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                                    <button onClick={exportWaitingToExcel} className="text-emerald-500 hover:scale-110 transition-all" title="Excel"><FiDownload size={14} /></button>
                                    <button onClick={exportWaitingToPDF} className="text-red-500 hover:scale-110 transition-all" title="PDF"><FiFileText size={14} /></button>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 relative min-h-[300px]">
                            {delayLoading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px] z-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div></div>
                            ) : delayAnalytics.waiting.length > 0 ? (
                                <HighchartsReact highcharts={Highcharts} options={waitingOptions} />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 text-slate-300">
                                    <FiZap size={40} className="mb-2 opacity-10" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">Excellent! No bottlenecks</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Status Overview</span>
                            <span className="text-[11px] font-black text-slate-700 uppercase">Impacted Tickets: {delayAnalytics.waiting.reduce((sum, i) => sum + parseInt(i.count), 0)}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-4 mt-4">
                    {/* Repeat Trends Card */}
                    <div className={`${cardStyle} h-full !p-0 overflow-hidden`}>
                        <div className="p-4 border-b border-gray-50 flex flex-col sm:flex-row items-center justify-between bg-white gap-4">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                                <FiTrendingUp className="text-red-500" /> Repeat Ticket Trends
                            </h4>
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <select
                                        value={newTrendsSelectedMonth}
                                        onChange={(e) => setNewTrendsSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[105px]"
                                    >
                                        <option value="all">All Month</option>
                                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="relative group">
                                    <select
                                        value={newTrendsSelectedYear}
                                        onChange={(e) => setNewTrendsSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[85px]"
                                    >
                                        <option value="all">All Year</option>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                                    <button onClick={exportRepeatToExcel} className="text-emerald-500 hover:scale-110 transition-all" title="Excel"><FiDownload size={14} /></button>
                                    <button onClick={() => exportTrendsToPDF('repeat')} className="text-red-500 hover:scale-110 transition-all" title="PDF"><FiFileText size={14} /></button>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 relative min-h-[400px]">
                            {trendsLoading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px] z-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div></div>
                            ) : (
                                <RepeatTrendChart
                                    data={repeatTrends}
                                    onDrilldown={fetchRepeatRejectDrilldown}
                                />
                            )}
                        </div>
                    </div>

                    {/* Testing Reject Trends Card */}
                    <div className={`${cardStyle} h-full !p-0 overflow-hidden`}>
                        <div className="p-4 border-b border-gray-50 flex flex-col sm:flex-row items-center justify-between bg-white gap-4">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                                <FiAlertTriangle className="text-orange-500" /> Testing Reject Status Trends
                            </h4>
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <select
                                        value={newTrendsSelectedMonth}
                                        onChange={(e) => setNewTrendsSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[105px]"
                                    >
                                        <option value="all">All Month</option>
                                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="relative group">
                                    <select
                                        value={newTrendsSelectedYear}
                                        onChange={(e) => setNewTrendsSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                        className="appearance-none pl-3 pr-4 py-1.5 text-[10px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer outline-none shadow-sm min-w-[85px]"
                                    >
                                        <option value="all">All Year</option>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                                </div>
                                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                                    <button onClick={exportRejectToExcel} className="text-emerald-500 hover:scale-110 transition-all" title="Excel"><FiDownload size={14} /></button>
                                    <button onClick={() => exportTrendsToPDF('reject')} className="text-red-500 hover:scale-110 transition-all" title="PDF"><FiFileText size={14} /></button>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 relative min-h-[400px]">
                            {trendsLoading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px] z-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>
                            ) : (
                                <TestingRejectStatusChart
                                    data={testingRejectTrends}
                                    onDrilldown={fetchRepeatRejectDrilldown}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {modalConfig.show && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full sm:w-[95vw] 2xl:max-w-[2200px] h-auto max-h-[100vh] sm:max-h-[96vh] flex flex-col overflow-hidden"
                        >
                            <div className="px-4 py-4 sm:px-8 sm:py-6 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-8 bg-white shrink-0">
                                <div>
                                    <div className="flex items-center gap-2">
                                        {modalAssigneeFilter && (
                                            <button
                                                onClick={() => setModalAssigneeFilter(null)}
                                                className="p-1 px-2 bg-red-100 text-red-600 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all flex items-center gap-1"
                                            >
                                                <FiX /> Clear Filter: {modalAssigneeFilter.name} {modalAssigneeFilter.source && modalAssigneeFilter.source !== 'Overall' ? `(${modalAssigneeFilter.source})` : ''}
                                            </button>
                                        )}
                                    </div>
                                    <h3 className="text-sm sm:text-lg md:text-xl font-black text-slate-800 tracking-tight whitespace-normal sm:whitespace-nowrap mt-1">{modalConfig.title}</h3>
                                    <motion.p
                                        animate={{ opacity: [1, 0.4, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                        className="text-[10px] font-bold text-red-500 mt-1 uppercase tracking-tighter"
                                    >
                                        Note: Click on Ticket ID or Title to view full details in the dashboard.
                                    </motion.p>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-4 shrink-0 w-full sm:w-auto justify-between sm:justify-end">
                                    <div className="hidden lg:flex items-center gap-2 border-r border-slate-100 pr-4">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Rows</span>
                                        <div className="relative group/rows flex items-center">
                                            <select
                                                value={modalItemsPerPage}
                                                onChange={(e) => {
                                                    const val = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                                                    setModalItemsPerPage(val);
                                                    setModalPage(1);
                                                }}
                                                className="pl-3 pr-8 py-1.5 text-[11px] font-black text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[70px] outline-none appearance-none"
                                            >
                                                {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                                                <option value="all">All</option>
                                            </select>
                                            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        </div>
                                    </div>

                                    <div className="relative group flex-1 sm:w-64 max-w-[200px] sm:max-w-none">
                                        <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-500 transition-colors" />
                                        <input
                                            type="text"
                                            placeholder="Search tickets..."
                                            value={modalSearch}
                                            onChange={(e) => { setModalSearch(e.target.value); setModalPage(1); }}
                                            className="pl-10 pr-4 h-10 w-full bg-slate-50 border-none rounded-xl text-[12px] font-bold text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-red-500/30 focus:bg-white transition-all outline-none shadow-inner"
                                        />
                                    </div>

                                    <div className="flex items-center gap-1 sm:gap-2">
                                        {/* Premium Chart Toggle */}
                                        <div className="flex items-center gap-2 mr-2 px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-100 shadow-inner group/toggle">
                                            <span className={`text-[9px] font-black uppercase tracking-widest ${showDrilldownChart ? 'text-red-500' : 'text-slate-400'}`}>Chart</span>
                                            <button
                                                onClick={() => setShowDrilldownChart(!showDrilldownChart)}
                                                className={`w-10 h-5 rounded-full relative transition-all duration-300 ${showDrilldownChart ? 'bg-red-500' : 'bg-slate-300'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300 ${showDrilldownChart ? 'left-6' : 'left-1'}`} />
                                            </button>
                                        </div>

                                        <div className="flex items-center bg-slate-50 p-1 rounded-2xl shadow-inner">
                                            <button
                                                onClick={() => exportDrilldownToExcel(filteredModalTickets, modalConfig.title)}
                                                title="Export to Excel"
                                                className="p-2 text-emerald-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                                            >
                                                <FiDownload size={18} />
                                            </button>
                                            <button
                                                onClick={() => exportDrilldownToPDF(filteredModalTickets, modalConfig.title)}
                                                title="Export to PDF"
                                                className="p-2 text-red-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                                            >
                                                <FiFileText size={18} />
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => { setModalConfig(prev => ({ ...prev, show: false })); setModalSearch(''); }}
                                            className="p-2.5 bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-xl transition-all"
                                        >
                                            <FiX size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div id="drilldown-modal-body" className="flex-1 flex flex-col p-2 sm:p-8 custom-scrollbar bg-slate-50/30 overflow-y-auto">
                                {modalConfig.loading ? (
                                    <div className="flex flex-col items-center py-24 gap-4">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                                        <p className="text-xs font-black text-red-600 uppercase tracking-[0.2em] animate-pulse">Syncing Details...</p>
                                    </div>
                                ) : filteredModalTickets.length > 0 ? (
                                    <div className="flex-1 flex flex-col min-h-0 gap-2 relative">
                                        {/* Drillaway Assignee Chart Section */}
                                        <AnimatePresence>
                                            {showDrilldownChart && modalConfig.chartData && modalConfig.chartData.length > 0 && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                    animate={{
                                                        opacity: 1,
                                                        height: drilldownChartHeight,
                                                        marginBottom: 4,
                                                        transition: { duration: isResizingDrilldown ? 0 : 0.3 } // Instant while dragging
                                                    }}
                                                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                    className={`bg-white rounded-3xl p-4 sm:p-6 border border-slate-100 shadow-sm flex flex-col gap-2 overflow-hidden relative shrink-0 ${isResizingDrilldown ? 'select-none' : ''}`}
                                                >
                                                    <div className="flex items-center gap-3 border-b border-slate-50 pb-2 mb-2">
                                                        <div className="p-2 bg-red-50 rounded-xl">
                                                            <FiActivity className="text-red-500" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">{modalConfig.type === 'reject' ? 'Developer Distribution' : modalConfig.type === 'repeat' ? 'Employee Distribution' : 'Assignee Distribution'}</h3>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Multi-Source Analytics View</p>
                                                        </div>
                                                    </div>

                                                    <div className={`flex-1 grid grid-cols-1 ${activeDonutCharts.length === 3 ? 'sm:grid-cols-3' : activeDonutCharts.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'} gap-4 items-center ${isResizingDrilldown ? 'pointer-events-none' : ''}`}>
                                                        {activeDonutCharts.map((chart) => (
                                                            <div key={chart.id} className="h-full flex flex-col items-center group/chart relative">
                                                                <div className="flex items-center gap-2 mb-1 px-3 py-1 bg-slate-50 border border-slate-100/50 rounded-full shadow-sm z-10 transition-all group-hover/chart:bg-white group-hover/chart:border-slate-200">
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${chart.badgeColor === 'blue' ? 'bg-blue-400 shadow-[0_0_5px_rgba(96,165,250,0.5)]' : chart.badgeColor === 'emerald' ? 'bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.5)]' : 'bg-amber-400 shadow-[0_0_5px_rgba(251,191,36,0.5)]'}`} />
                                                                    <span className="text-[9px] font-black text-slate-800 uppercase tracking-widest">{chart.labelFull}</span>
                                                                </div>
                                                                <div className="w-full h-full min-h-[220px]">
                                                                    <HighchartsReact
                                                                        highcharts={Highcharts}
                                                                        options={getPremiumDonutOptions(chart.data, chart.label, chart.colorSet)}
                                                                        containerProps={{ style: { height: '100%', width: '100%' } }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Vertical Resize Handle at the bottom of chart section */}
                                                    <div
                                                        onMouseDown={startResizing}
                                                        onDoubleClick={resetDrilldownSize}
                                                        className={`absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize transition-all flex items-center justify-center z-20 group/handle ${isResizingDrilldown ? 'bg-slate-800/10' : 'hover:bg-slate-50'}`}
                                                        title="Drag up/down to resize | Double-click to reset"
                                                    >
                                                        <div className="flex flex-col items-center">
                                                            <div className={`flex items-center justify-center bg-white border border-slate-100 shadow-lg rounded-full px-2 py-1 transition-all ${isResizingDrilldown ? 'scale-125 border-red-400 bg-red-50 shadow-red-200/50' : 'group-hover/handle:scale-110 shadow-slate-200/50'}`}>
                                                                <FiMove size={14} className={`${isResizingDrilldown ? 'text-red-500' : 'text-slate-500 group-hover/handle:text-red-500'}`} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                                            <div className="overflow-auto flex-1 custom-scrollbar">
                                                <table className="w-full text-left border-collapse">
                                                    <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10 shadow-sm">
                                                        <tr>
                                                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">S.No</th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('id')}
                                                            >
                                                                ID {modalSortConfig.key === 'id' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('title')}
                                                            >
                                                                Title {modalSortConfig.key === 'title' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors text-center"
                                                                onClick={() => handleModalSort('origin')}
                                                            >
                                                                Origin {modalSortConfig.key === 'origin' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            {!modalConfig.title.includes("(Customer)") && (
                                                                <th
                                                                    className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center cursor-pointer hover:text-red-600 transition-colors"
                                                                    onClick={() => handleModalSort('customer_code')}
                                                                >
                                                                    Customer {modalSortConfig.key === 'customer_code' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                                </th>
                                                            )}
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('status')}
                                                            >
                                                                Status {modalSortConfig.key === 'status' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('issue_type')}
                                                            >
                                                                Issue Type {modalSortConfig.key === 'issue_type' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('currently_working')}
                                                            >
                                                                {modalConfig.type === 'reject' ? 'Developer' : modalConfig.type === 'repeat' ? 'Employee' : 'Assigned To'} {modalSortConfig.key === 'currently_working' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            {modalConfig.type !== 'reject' && (
                                                                <th
                                                                    className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors text-center"
                                                                    onClick={() => handleModalSort('aging_display')}
                                                                >
                                                                    Aging {modalSortConfig.key === 'aging_display' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                                </th>
                                                            )}
                                                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                                                {modalConfig.type === 'reject' ? 'Tested By' : 'All Collaborators'}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('created_date')}
                                                            >
                                                                Created {modalSortConfig.key === 'created_date' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('updated_date')}
                                                            >
                                                                Last Updated {modalSortConfig.key === 'updated_date' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {(() => {
                                                            const pSize = modalItemsPerPage === 'all' ? sortedModalTickets.length : modalItemsPerPage;
                                                            return sortedModalTickets.slice((modalPage - 1) * pSize, modalPage * pSize).map((t, i) => (
                                                                <tr key={i} className="hover:bg-blue-50/30 transition-colors group">
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[11px] font-bold text-slate-400">{(modalPage - 1) * pSize + i + 1}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <button
                                                                            onClick={() => window.open(`/tickets?tid=${t.id}`, '_blank')}
                                                                            className="text-[11px] font-black text-red-600 bg-red-50 px-2 py-1 rounded-lg border border-red-100 hover:bg-red-600 hover:text-white hover:shadow-md transition-all active:scale-95"
                                                                            title="Open Ticket"
                                                                        >
                                                                            #{t.id}
                                                                        </button>
                                                                    </td>
                                                                    <td className="px-6 py-4 min-w-[300px]">
                                                                        <div className="flex flex-col gap-1 group/copy">
                                                                            <div className="flex items-start justify-between gap-2">
                                                                                <p
                                                                                    onClick={() => window.open(`/tickets?tid=${t.id}`, '_blank')}
                                                                                    className="text-[13px] font-bold text-slate-700 leading-snug cursor-pointer hover:text-red-600 hover:underline transition-all"
                                                                                >
                                                                                    {t.title}
                                                                                </p>
                                                                                <button
                                                                                    onClick={() => handleCopyTitle(t.id, t.title)}
                                                                                    className="shrink-0 p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all active:scale-90"
                                                                                    title="Copy Title"
                                                                                >
                                                                                    <FiCopy size={14} />
                                                                                </button>
                                                                            </div>
                                                                            {copiedTicketId === t.id && (
                                                                                <motion.div
                                                                                    initial={{ opacity: 0, y: -5 }}
                                                                                    animate={{ opacity: 1, y: 0 }}
                                                                                    className="flex items-center gap-1.5 text-emerald-600"
                                                                                >
                                                                                    <span className="text-[10px] font-black uppercase tracking-wider">✓ Copied</span>
                                                                                </motion.div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-center">
                                                                        <span className={`text-[10px] font-black px-2 py-1 rounded-md uppercase border ${t.origin === 'Internal' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                                                                            {t.origin}
                                                                        </span>
                                                                    </td>
                                                                    {!modalConfig.title.includes("(Customer)") && (
                                                                        <td className="px-6 py-4 text-center">
                                                                            <span className="text-[10px] font-black bg-slate-50 text-slate-600 px-2 py-1 rounded-md uppercase border border-slate-100 shadow-sm">{t.customer_code || 'N/A'}</span>
                                                                        </td>
                                                                    )}
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-tighter">
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                                                                            {t.status}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[10px] font-bold text-slate-600">{t.issue_type || '—'}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[10px] font-bold text-slate-600" title={t.currently_working}>{t.currently_working || 'N/A'}</span>
                                                                    </td>
                                                                    {modalConfig.type !== 'reject' && (
                                                                        <td className="px-6 py-4 text-center">
                                                                            <div className="flex flex-col items-center gap-1">
                                                                                {/* Total Aging */}
                                                                                <span className="text-[10px] font-black bg-amber-50 text-amber-600 px-2 py-1 rounded-md border border-amber-100 whitespace-nowrap">
                                                                                    {t.aging_display}
                                                                                </span>
                                                                                {/* On Hold split — only shown when ticket has spent time On Hold */}
                                                                                {t.on_hold_display && (
                                                                                    <>
                                                                                        <span className="text-[9px] font-black bg-orange-50 text-orange-500 px-2 py-0.5 rounded-md border border-orange-100 whitespace-nowrap">
                                                                                            ⏸ {t.on_hold_display} on hold
                                                                                        </span>
                                                                                        <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-md border border-emerald-100 whitespace-nowrap">
                                                                                            ✓ {t.net_aging_display} active
                                                                                        </span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    )}
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[10px] font-medium text-slate-500" title={t.collaborators}>{t.collaborators || 'N/A'}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[10px] font-bold text-slate-600">{t.created_date ? format(new Date(t.created_date), 'dd/MM/yyyy') : 'N/A'}</span>
                                                                            <span className="text-[9px] text-slate-400 font-medium">{t.created_date ? format(new Date(t.created_date), 'HH:mm') : ''}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[10px] font-bold text-slate-600">{t.updated_date ? format(new Date(t.updated_date), 'dd/MM/yyyy') : 'N/A'}</span>
                                                                            <span className="text-[9px] text-slate-400 font-medium">{t.updated_date ? format(new Date(t.updated_date), 'HH:mm') : ''}</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ));
                                                        })()}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {/* Functional Modal Pagination Footer */}
                                            <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                                                        Showing {(() => {
                                                            const pSize = modalItemsPerPage === 'all' ? filteredModalTickets.length : modalItemsPerPage;
                                                            return Math.min(filteredModalTickets.length, (modalPage - 1) * pSize + 1);
                                                        })()} to {(() => {
                                                            const pSize = modalItemsPerPage === 'all' ? filteredModalTickets.length : modalItemsPerPage;
                                                            return Math.min(filteredModalTickets.length, modalPage * pSize);
                                                        })()} of {filteredModalTickets.length} results
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => setModalPage(p => Math.max(1, p - 1))}
                                                        disabled={modalPage === 1}
                                                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed border border-slate-100 hover:border-red-100 shadow-sm"
                                                    >
                                                        <FiChevronLeft size={20} />
                                                    </button>

                                                    <div className="flex items-center gap-1.5 px-2">
                                                        {(() => {
                                                            const total = filteredModalTickets.length;
                                                            const perPage = modalItemsPerPage === 'all' ? total : modalItemsPerPage;
                                                            const totalP = Math.ceil(total / (perPage || 1));
                                                            if (totalP <= 0) return null;

                                                            let pages = [];
                                                            const maxVisible = 4;
                                                            let start = Math.max(1, modalPage - 1);

                                                            if (start + maxVisible - 1 > totalP) {
                                                                start = Math.max(1, totalP - maxVisible + 1);
                                                            }

                                                            const end = Math.min(start + maxVisible - 1, totalP);
                                                            for (let i = start; i <= end; i++) {
                                                                pages.push(i);
                                                            }

                                                            return pages.map((p) => (
                                                                <button
                                                                    key={p}
                                                                    onClick={() => setModalPage(p)}
                                                                    className={`w-9 h-9 flex items-center justify-center rounded-xl text-xs font-black transition-all ${modalPage === p
                                                                        ? 'bg-red-600 text-white shadow-lg shadow-red-200'
                                                                        : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-100'
                                                                        }`}
                                                                >
                                                                    {p}
                                                                </button>
                                                            ));
                                                        })()}
                                                    </div>

                                                    <button
                                                        onClick={() => setModalPage(p => p + 1)}
                                                        disabled={modalItemsPerPage === 'all' || modalPage * modalItemsPerPage >= filteredModalTickets.length}
                                                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed border border-slate-100 hover:border-red-100 shadow-sm"
                                                    >
                                                        <FiChevronRight size={20} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-24 text-slate-300">
                                        <FiX className="text-6xl mx-auto mb-4 opacity-10" />
                                        <p className="text-xs font-black uppercase tracking-[0.3em]">No Data Found</p>
                                    </div>
                                )}
                            </div>

                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f8fafc; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 20px; border: 2px solid #f8fafc; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
                .lt-datepicker {
                    border: 1px solid #dbe4f0;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.14);
                    font-family: inherit;
                }
                .lt-datepicker .react-datepicker__header {
                    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
                    border-bottom: 1px solid #e2e8f0;
                }
                .lt-datepicker .react-datepicker__current-month {
                    font-size: 14px;
                    font-weight: 700;
                    color: #0f172a;
                }
                .lt-datepicker .react-datepicker__day-name,
                .lt-datepicker .react-datepicker__day {
                    width: 2rem;
                    line-height: 2rem;
                    margin: 0.15rem;
                    border-radius: 0.5rem;
                }
                .lt-datepicker .react-datepicker__day--selected,
                .lt-datepicker .react-datepicker__day--keyboard-selected {
                    background: #2563eb;
                    color: #ffffff;
                }
                .lt-datepicker .react-datepicker__day:hover {
                    background: #dbeafe;
                }
                .lt-datepicker .react-datepicker__triangle {
                    display: none;
                }
                .fullscreen-modal {
                    background: #f8fafc;
                    padding: 40px;
                    border-radius: 30px;
                    width: 95vw;
                    max-width: 1400px;
                    box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
                }
            `}</style>

            <AnimatePresence>
                {isFullscreen && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/90 backdrop-blur-md p-6">
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fullscreen-modal relative"
                        >
                            <button
                                onClick={() => setIsFullscreen(false)}
                                className="absolute top-6 right-6 p-3 bg-white text-slate-400 hover:text-red-500 rounded-2xl shadow-xl transition-all z-20"
                            >
                                <FiX size={24} />
                            </button>
                            <div className="w-full">
                                <HighchartsReact highcharts={Highcharts} options={trendChartOptions} />
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Leadtickettrends;

//completed